import React from "react";
import { motion } from "framer-motion";
import clsx from "clsx";

export type LoadingPattern =
  | "text"
  | "card"
  | "list"
  | "table"
  | "avatar"
  | "button"
  | "image"
  | "custom";

interface LoadingPlaceholderProps {
  pattern?: LoadingPattern;
  text?: string;
  className?: string;
  count?: number;
  height?: string | number;
  width?: string | number;
  animated?: boolean;
  speed?: number;
  customContent?: React.ReactNode;
}

const LoadingPlaceholder: React.FC<LoadingPlaceholderProps> = ({
  pattern = "text",
  text,
  className = "",
  count = 1,
  height,
  width,
  animated = true,
  speed = 1.5,
  customContent,
}) => {
  // Animation variants
  const shimmerVariants = {
    initial: { x: "-100%" },
    animate: {
      x: "100%",
      transition: {
        duration: speed * 1.5,
        repeat: Infinity,
        ease: "easeInOut",
      },
    },
  };

  const pulseVariants = {
    initial: { opacity: 0.85 },
    animate: {
      opacity: [0.85, 1, 0.85],
      transition: {
        duration: speed * 1.2,
        repeat: Infinity,
        ease: "easeInOut",
      },
    },
  };

  // Base shimmer component
  const Shimmer = () => (
    <motion.div
      className="absolute inset-0"
      style={{
        background: `linear-gradient(90deg, 
          transparent 0%, 
          transparent 20%, 
          rgba(255, 255, 255, 0.04) 50%, 
          transparent 80%, 
          transparent 100%)`,
      }}
      variants={animated ? shimmerVariants : undefined}
      initial="initial"
      animate="animate"
    />
  );

  // Base placeholder component
  const PlaceholderItem = ({
    itemClassName = "",
    itemHeight,
    itemWidth,
  }: {
    itemClassName?: string;
    itemHeight?: string | number;
    itemWidth?: string | number;
  }) => (
    <motion.div
      className={clsx(
        "relative overflow-hidden rounded-md bg-gray-200 dark:bg-slate-700",
        itemClassName
      )}
      style={{
        height: itemHeight || height,
        width: itemWidth || width,
      }}
      variants={animated ? pulseVariants : undefined}
      initial="initial"
      animate="animate"
    >
      {animated && <Shimmer />}
    </motion.div>
  );

  // Pattern-specific components
  const renderTextPattern = () => (
    <div className="space-y-2">
      {Array.from({ length: count }).map((_, index) => (
        <PlaceholderItem
          key={index}
          itemHeight={index === 0 ? "1.5rem" : "1rem"}
          itemWidth={index === 0 ? "80%" : "60%"}
        />
      ))}
    </div>
  );

  const renderCardPattern = () => (
    <div className="space-y-3">
      {Array.from({ length: count }).map((_, index) => (
        <div
          key={index}
          className="p-4 border border-gray-200 dark:border-slate-700 rounded-lg"
        >
          <div className="space-y-3">
            <PlaceholderItem itemHeight="1.5rem" itemWidth="60%" />
            <PlaceholderItem itemHeight="1rem" itemWidth="100%" />
            <PlaceholderItem itemHeight="1rem" itemWidth="80%" />
            <div className="flex gap-2">
              <PlaceholderItem itemHeight="2rem" itemWidth="4rem" />
              <PlaceholderItem itemHeight="2rem" itemWidth="4rem" />
            </div>
          </div>
        </div>
      ))}
    </div>
  );

  const renderListPattern = () => (
    <div className="space-y-2">
      {Array.from({ length: count }).map((_, index) => (
        <div key={index} className="flex items-center gap-3">
          <PlaceholderItem itemHeight="2.5rem" itemWidth="2.5rem" />
          <div className="flex-1 space-y-1">
            <PlaceholderItem itemHeight="1rem" itemWidth="70%" />
            <PlaceholderItem itemHeight="0.875rem" itemWidth="50%" />
          </div>
        </div>
      ))}
    </div>
  );

  const renderTablePattern = () => (
    <div className="space-y-2">
      {/* Header */}
      <div className="flex gap-2 pb-2 border-b border-gray-200 dark:border-slate-700">
        {Array.from({ length: 4 }).map((_, index) => (
          <PlaceholderItem
            key={index}
            itemHeight="1.25rem"
            itemWidth="100%"
            itemClassName="flex-1"
          />
        ))}
      </div>
      {/* Rows */}
      {Array.from({ length: count }).map((_, rowIndex) => (
        <div key={rowIndex} className="flex gap-2 py-2">
          {Array.from({ length: 4 }).map((_, colIndex) => (
            <PlaceholderItem
              key={colIndex}
              itemHeight="1rem"
              itemWidth="100%"
              itemClassName="flex-1"
            />
          ))}
        </div>
      ))}
    </div>
  );

  const renderAvatarPattern = () => (
    <div className="flex items-center gap-3">
      <PlaceholderItem
        itemHeight="3rem"
        itemWidth="3rem"
        itemClassName="rounded-full"
      />
      <div className="flex-1 space-y-1">
        <PlaceholderItem itemHeight="1.25rem" itemWidth="60%" />
        <PlaceholderItem itemHeight="1rem" itemWidth="40%" />
      </div>
    </div>
  );

  const renderButtonPattern = () => (
    <PlaceholderItem itemHeight="2.5rem" itemWidth="8rem" />
  );

  const renderImagePattern = () => (
    <PlaceholderItem
      itemHeight="12rem"
      itemWidth="100%"
      itemClassName="rounded-lg"
    />
  );

  const renderCustomPattern = () => (
    <div className="space-y-3">
      {customContent || (
        <div className="text-center py-8">
          <PlaceholderItem
            itemHeight="4rem"
            itemWidth="4rem"
            itemClassName="mx-auto rounded-full"
          />
          <div className="mt-4 space-y-2">
            <PlaceholderItem
              itemHeight="1.5rem"
              itemWidth="60%"
              itemClassName="mx-auto"
            />
            <PlaceholderItem
              itemHeight="1rem"
              itemWidth="40%"
              itemClassName="mx-auto"
            />
          </div>
        </div>
      )}
    </div>
  );

  // Pattern renderer
  const renderPattern = () => {
    switch (pattern) {
      case "text":
        return renderTextPattern();
      case "card":
        return renderCardPattern();
      case "list":
        return renderListPattern();
      case "table":
        return renderTablePattern();
      case "avatar":
        return renderAvatarPattern();
      case "button":
        return renderButtonPattern();
      case "image":
        return renderImagePattern();
      case "custom":
        return renderCustomPattern();
      default:
        return renderTextPattern();
    }
  };

  return (
    <div className={clsx("w-full", className)}>
      {text && (
        <div className="mb-3 text-center">
          <motion.p
            className="text-sm text-gray-500 dark:text-gray-400"
            variants={animated ? pulseVariants : undefined}
            initial="initial"
            animate="animate"
          >
            {text}
          </motion.p>
        </div>
      )}
      {renderPattern()}
    </div>
  );
};

export default LoadingPlaceholder;
